import { useMemo, useState } from 'react';
import type { Severity } from '@/lib/postSimulator';
import type { CarouselInputs, FileKind } from '@/lib/carouselCheck';
import { EMPTY_INPUTS, FILE_KINDS, SIZE_PRESETS, evaluate, reportText } from '@/lib/carouselCheck';

const SEVERITY_LABEL: Record<Severity, string> = {
  problem: 'À corriger',
  warning: 'À surveiller',
  ok: 'OK',
};

const SOURCE_LABEL = {
  official: 'Règle LinkedIn',
  editorial: 'Repère NeverZero',
} as const;

// Un carrousel de 8 pages avec deux défauts typiques : couverture bavarde, page 4 trop dense, texte trop petit.
const SAMPLE: CarouselInputs = {
  kind: 'pdf',
  pages: '8',
  sizeMb: '14',
  width: '1920',
  height: '1080',
  fontPx: '28',
  sameSize: true,
  flattened: true,
  titled: true,
  slides: `Comment nous avons restructuré notre processus de prospection LinkedIn au cours des douze derniers mois
---
Le constat : 40 messages par semaine, 2 réponses.
---
Ce qui n'allait pas : message générique, aucun lien avec le profil de la personne.
---
La nouvelle méthode : nous commençons maintenant chaque échange par un commentaire sous un de ses posts récents, nous attendons deux jours avant d'envoyer la demande de connexion, nous mentionnons ce commentaire dans la note, nous posons une seule question ouverte liée à son secteur, nous relançons une seule fois après sept jours avec un contenu utile et jamais avec un rappel, puis nous arrêtons.
---
Résultat : 11 réponses sur 40.
---
Ce qu'on garde : une question, un commentaire, une relance.
---
Ce qu'on a arrêté : les messages copiés-collés.
---
Et vous, comment démarrez-vous une conversation ? Dites-le en commentaire.`,
};

export function CarouselChecker() {
  const [input, setInput] = useState<CarouselInputs>(EMPTY_INPUTS);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => evaluate(input), [input]);
  const touched = JSON.stringify(input) !== JSON.stringify(EMPTY_INPUTS);

  const update = (patch: Partial<CarouselInputs>) => {
    setInput((prev) => ({ ...prev, ...patch }));
    setCopied(false);
  };

  const copyResult = () => {
    try {
      navigator.clipboard
        .writeText(reportText(result))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste inerte.
        });
    } catch {
      // Pas de Clipboard API : on n'interrompt rien.
    }
  };

  const show = result.verdict !== 'incomplete';

  return (
    <div className="nz-sim nz-carousel">
      <div className="nz-calc__label" id="nz-carousel-kind">
        Format du fichier
      </div>
      <div className="nz-sim__tabs nz-calc__formats" role="group" aria-labelledby="nz-carousel-kind">
        {(Object.keys(FILE_KINDS) as FileKind[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`nz-sim__tab${input.kind === key ? ' nz-sim__tab--on' : ''}`}
            aria-pressed={input.kind === key}
            onClick={() => update({ kind: input.kind === key ? null : key })}
          >
            {FILE_KINDS[key]}
          </button>
        ))}
      </div>

      <div className="nz-calc__grid nz-carousel__grid">
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-carousel-pages">
            Nombre de pages
          </label>
          <input
            id="nz-carousel-pages"
            className="nz-sim__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="10"
            value={input.pages}
            onChange={(event) => update({ pages: event.target.value })}
          />
          <small className="nz-calc__hint">Plafond LinkedIn : 300</small>
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-carousel-size">
            Poids du fichier (Mo)
          </label>
          <input
            id="nz-carousel-size"
            className="nz-sim__input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="8"
            value={input.sizeMb}
            onChange={(event) => update({ sizeMb: event.target.value })}
          />
          <small className="nz-calc__hint">Plafond LinkedIn : 100 Mo</small>
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-carousel-width">
            Largeur d’une page (px)
          </label>
          <input
            id="nz-carousel-width"
            className="nz-sim__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1080"
            value={input.width}
            onChange={(event) => update({ width: event.target.value })}
          />
        </div>
        <div className="nz-calc__field">
          <label className="nz-sim__label" htmlFor="nz-carousel-height">
            Hauteur d’une page (px)
          </label>
          <input
            id="nz-carousel-height"
            className="nz-sim__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1350"
            value={input.height}
            onChange={(event) => update({ height: event.target.value })}
          />
        </div>
      </div>

      <div className="nz-carousel__presets" role="group" aria-label="Formats de page courants">
        {SIZE_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className="nz-sim__tab"
            onClick={() => update({ width: preset.width, height: preset.height })}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="nz-calc__field nz-carousel__font">
        <label className="nz-sim__label" htmlFor="nz-carousel-font">
          Corps du texte courant (px), facultatif
        </label>
        <input
          id="nz-carousel-font"
          className="nz-sim__input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="36"
          value={input.fontPx}
          onChange={(event) => update({ fontPx: event.target.value })}
        />
        <small className="nz-calc__hint">
          {result.minFontPx !== null
            ? `À cette largeur, comptez ${result.minFontPx} px au minimum pour rester lisible sur téléphone.`
            : 'Saisissez la largeur : l’outil calcule le corps minimal lisible sur téléphone.'}
        </small>
      </div>

      <fieldset className="nz-carousel__checks">
        <legend className="nz-sim__label">Préparation du PDF</legend>
        <label className="nz-carousel__check">
          <input type="checkbox" checked={input.sameSize} onChange={(event) => update({ sameSize: event.target.checked })} />
          Toutes les pages ont la même taille
        </label>
        <label className="nz-carousel__check">
          <input type="checkbox" checked={input.flattened} onChange={(event) => update({ flattened: event.target.checked })} />
          Le PDF est aplati (pas de calques multiples)
        </label>
        <label className="nz-carousel__check">
          <input type="checkbox" checked={input.titled} onChange={(event) => update({ titled: event.target.checked })} />
          J’ai un titre pour le document
        </label>
      </fieldset>

      <label className="nz-sim__label" htmlFor="nz-carousel-slides">
        Texte des pages, facultatif
      </label>
      <textarea
        id="nz-carousel-slides"
        className="nz-sim__input"
        rows={6}
        value={input.slides}
        placeholder={'Collez le texte page par page, en séparant chaque page par une ligne « --- » (ou une ligne vide).\nL’outil contrôle la couverture, la densité et la dernière page.'}
        onChange={(event) => update({ slides: event.target.value })}
      />

      <div className="nz-sim__bar">
        <button type="button" className="nz-wizard__back nz-sim__sample" onClick={() => setInput(touched ? EMPTY_INPUTS : SAMPLE)}>
          {touched ? 'Tout effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      <div className="nz-calc__result" aria-live="polite">
        {show ? (
          <>
            <h2 className={`nz-calc__headline nz-carousel__headline--${result.verdict}`}>{result.headline}</h2>
            <p>{result.summary}</p>
            <ul className="nz-sim__findings">
              {result.checks.map((check) => (
                <li key={check.id} className={`nz-sim__finding nz-sim__finding--${check.severity}`}>
                  <span className="nz-sim__badge">{SEVERITY_LABEL[check.severity]}</span>{' '}
                  <span className="nz-carousel__source">{SOURCE_LABEL[check.source]}</span>
                  <strong>{check.title}</strong>
                  <p>{check.detail}</p>
                  {check.fix ? <p className="nz-sim__fix">→ {check.fix}</p> : null}
                </li>
              ))}
            </ul>
            <div className="nz-wizard__actions">
              <button type="button" className="nz-btn-secondary" onClick={copyResult}>
                {copied ? 'Copié' : 'Copier le bilan'}
              </button>
            </div>
          </>
        ) : (
          <p className="nz-calc__empty">
            Renseignez le nombre de pages, le poids ou les dimensions : le bilan se met à jour en direct. Collez le texte
            des pages pour contrôler aussi la couverture et la densité.
          </p>
        )}
      </div>
    </div>
  );
}
